/**
 * IndexedDB via Dexie — local-first cache of user data.
 *
 * Tables mirror the server schema but only the current user's data is cached.
 * Writes are always done locally first with `syncStatus='pending'`, then pushed
 * to the server. On success the row is flipped to `syncStatus='synced'`.
 *
 * The DB is cleared on sign-out (different users should not see each other's data).
 */
import Dexie, { type Table } from 'dexie'

export type SyncStatus = 'synced' | 'pending'

export interface LocalUser {
  id: string
  phone: string
  role: 'user' | 'admin'
}

export interface LocalSession {
  id: string
  exerciseKey: string
  exerciseType: 'aerobic' | 'anaerobic'
  startedAt: string   // ISO
  endedAt: string     // ISO
  durationSeconds: number
  summary: Record<string, unknown> | null
  createdAt: string
  syncStatus: SyncStatus
  /** True when the row is locally deleted, pending propagation to the server.
   * Reads should filter these out so they vanish from the UI immediately. */
  pendingDelete?: boolean
  /** True when a previously-synced row has been edited locally and needs a
   * targeted /update push (vs the default /save which would no-op on a
   * known id). Only set on rows whose id is already on the server. */
  pendingEdit?: boolean
}

export interface LocalUserExercise {
  exerciseKey: string     // primary key — unique per user
  sortOrder: number
  createdAt: string
  syncStatus: SyncStatus
  /** true if this row is a local deletion pending server sync */
  pendingDelete?: boolean
}

export interface LocalExerciseSettings {
  exerciseKey: string     // primary key
  plankDuration: number | null
  plankRestDuration: number | null
  curlRestDuration: number | null
  curlWeight: number | null
  pushUpRestDuration: number | null
  dumbbellPressRestDuration: number | null
  dumbbellPressWeight: number | null
  gluteBridgeRestDuration: number | null
  squatRestDuration: number | null
  syncStatus: SyncStatus
}

export type CustomExerciseTemplate = 'timer' | 'laps_timer' | 'countdown' | 'manual_reps'

export interface LocalCustomExercise {
  exerciseKey: string     // primary key — 'custom_<uuid>' or 'preset_<slug>'
  name: string
  icon: string            // lucide icon name, e.g. 'lucide:dumbbell'
  template: CustomExerciseTemplate
  exerciseType: 'aerobic' | 'anaerobic'
  restDuration: number | null     // seconds between sets; null for timer/laps_timer
  defaultDuration: number | null  // seconds per set; only for countdown template
  weight: number | null           // kg; only for sets_with_rest template
  // Quick-rep presets — only meaningful for the manual_reps template.
  quickReps1: number | null
  quickReps2: number | null
  quickReps3: number | null
  sortOrder: number
  createdAt: string
  syncStatus: SyncStatus
  pendingDelete?: boolean
}

export interface LocalMeta {
  key: string
  value: unknown
}

class FitTimeDB extends Dexie {
  sessions!: Table<LocalSession, string>
  userExercises!: Table<LocalUserExercise, string>
  exerciseSettings!: Table<LocalExerciseSettings, string>
  customExercises!: Table<LocalCustomExercise, string>
  meta!: Table<LocalMeta, string>

  constructor() {
    super('fit-time')
    this.version(1).stores({
      sessions: 'id, startedAt, syncStatus',
      userExercises: 'exerciseKey, syncStatus',
      exerciseSettings: 'exerciseKey, syncStatus',
      meta: 'key',
    })
    // v2: custom exercises (user-defined with template + icon)
    this.version(2).stores({
      sessions: 'id, startedAt, syncStatus',
      userExercises: 'exerciseKey, syncStatus',
      exerciseSettings: 'exerciseKey, syncStatus',
      customExercises: 'exerciseKey, syncStatus, sortOrder',
      meta: 'key',
    })
    // v3: add quickReps1/2/3 fields to customExercises (manual_reps template).
    this.version(3)
      .stores({
        sessions: 'id, startedAt, syncStatus',
        userExercises: 'exerciseKey, syncStatus',
        exerciseSettings: 'exerciseKey, syncStatus',
        customExercises: 'exerciseKey, syncStatus, sortOrder',
        meta: 'key',
      })
      .upgrade(async (tx) => {
        await tx.table('customExercises').toCollection().modify((row: Partial<LocalCustomExercise>) => {
          if (row.quickReps1 === undefined) row.quickReps1 = null
          if (row.quickReps2 === undefined) row.quickReps2 = null
          if (row.quickReps3 === undefined) row.quickReps3 = null
        })
      })
    // v4: add weight (sets_with_rest) and defaultDuration (countdown) fields.
    this.version(4)
      .stores({
        sessions: 'id, startedAt, syncStatus',
        userExercises: 'exerciseKey, syncStatus',
        exerciseSettings: 'exerciseKey, syncStatus',
        customExercises: 'exerciseKey, syncStatus, sortOrder',
        meta: 'key',
      })
      .upgrade(async (tx) => {
        await tx.table('customExercises').toCollection().modify((row: Partial<LocalCustomExercise>) => {
          if (row.weight === undefined) row.weight = null
          if (row.defaultDuration === undefined) row.defaultDuration = null
        })
      })
  }
}

export const db = new FitTimeDB()

// ============================================================================
// Meta helpers
// ============================================================================

const META_USER = 'user'
const META_BOOTSTRAPPED = 'bootstrapped'

export async function getUser(): Promise<LocalUser | null> {
  const row = await db.meta.get(META_USER)
  return (row?.value as LocalUser) ?? null
}

export async function setUser(user: LocalUser): Promise<void> {
  await db.meta.put({ key: META_USER, value: user })
}

export async function clearUser(): Promise<void> {
  await db.meta.delete(META_USER)
}

export async function isBootstrapped(): Promise<boolean> {
  const row = await db.meta.get(META_BOOTSTRAPPED)
  return row?.value === true
}

export async function markBootstrapped(): Promise<void> {
  await db.meta.put({ key: META_BOOTSTRAPPED, value: true })
}

// ============================================================================
// Active session — persisted "in-progress workout" for timer/laps_timer.
// Lets a user close the tab / kill the PWA / accidentally hit back and still
// recover the running workout on next entry.
// ============================================================================

const META_ACTIVE_SESSION = 'active_session'

export interface ActiveSession {
  exerciseKey: string
  template: 'timer' | 'laps_timer'
  startedAtIso: string
  /** Accumulated paused milliseconds across past pause/resume cycles. */
  pausedTotalMs: number
  /** ISO of the start of the current pause; null when running. */
  pausedAtIso: string | null
  /** Recorded lap times in ms (only used by laps_timer). */
  laps?: number[]
}

export async function getActiveSession(): Promise<ActiveSession | null> {
  const row = await db.meta.get(META_ACTIVE_SESSION)
  return (row?.value as ActiveSession) ?? null
}

export async function setActiveSession(s: ActiveSession): Promise<void> {
  await db.meta.put({ key: META_ACTIVE_SESSION, value: s })
}

export async function clearActiveSession(): Promise<void> {
  await db.meta.delete(META_ACTIVE_SESSION)
}

// ============================================================================
// Weekly plan — single record per user (free text per weekday).
// Stored in the meta table because it's a singleton, not a collection.
// ============================================================================

const META_WEEKLY_PLAN = 'weekly_plan'

export interface LocalWeeklyPlan {
  mon: string | null
  tue: string | null
  wed: string | null
  thu: string | null
  fri: string | null
  sat: string | null
  sun: string | null
  updatedAt: string
  syncStatus: SyncStatus
}

export const EMPTY_WEEKLY_PLAN: LocalWeeklyPlan = {
  mon: null, tue: null, wed: null, thu: null, fri: null, sat: null, sun: null,
  updatedAt: '1970-01-01T00:00:00.000Z',
  syncStatus: 'synced',
}

export async function getWeeklyPlan(): Promise<LocalWeeklyPlan> {
  const row = await db.meta.get(META_WEEKLY_PLAN)
  return (row?.value as LocalWeeklyPlan) ?? { ...EMPTY_WEEKLY_PLAN }
}

export async function setWeeklyPlan(plan: LocalWeeklyPlan): Promise<void> {
  await db.meta.put({ key: META_WEEKLY_PLAN, value: plan })
}

/**
 * One-time prune of aerobic sessions shorter than 60s (historical data from
 * before the <60s save guard was added). Idempotent via meta flag.
 */
const META_MIGRATION_PRUNE_SHORT_AEROBIC = 'migration:prune-short-aerobic-v1'

export async function pruneShortAerobicOnce(): Promise<number> {
  const done = await db.meta.get(META_MIGRATION_PRUNE_SHORT_AEROBIC)
  if (done?.value === true) return 0
  const victims = await db.sessions
    .filter(s => s.exerciseType === 'aerobic' && s.durationSeconds < 60)
    .toArray()
  if (victims.length > 0) {
    await db.sessions.bulkDelete(victims.map(s => s.id))
  }
  await db.meta.put({ key: META_MIGRATION_PRUNE_SHORT_AEROBIC, value: true })
  return victims.length
}

/** Clears all user-scoped data. Call on sign-out or account switch. */
export async function clearAllLocalData(): Promise<void> {
  await db.transaction('rw', [db.sessions, db.userExercises, db.exerciseSettings, db.customExercises, db.meta], async () => {
    await Promise.all([
      db.sessions.clear(),
      db.userExercises.clear(),
      db.exerciseSettings.clear(),
      db.customExercises.clear(),
      db.meta.clear(),
    ])
  })
}
